import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Info,
  Shield,
  Timer,
  Package,
  Bed,
  FileDown,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { SummaryBanner, SectionBox, EmptyBlock } from "@/components/DashboardBits";
import { fmtCurrency, fmtPct } from "@/lib/format";

const TONE = {
  positive: { border: "border-l-green-600", bg: "bg-green-50/40", badge: "bg-green-100 text-green-800", icon: TrendingUp },
  negative: { border: "border-l-[#DC2626]", bg: "bg-red-50/30", badge: "bg-red-100 text-[#991B1B]", icon: TrendingDown },
  warning: { border: "border-l-amber-500", bg: "bg-amber-50/40", badge: "bg-amber-100 text-amber-900", icon: AlertTriangle },
  info: { border: "border-l-[#5E3BFF]", bg: "bg-violet-50/50", badge: "bg-violet-100 text-[#5E3BFF]", icon: Info },
};

const SCENARIO_ICON = {
  shield: Shield,
  timer: Timer,
  package: Package,
  "trending-up": TrendingUp,
  bed: Bed,
};

const RISK_COLOR = {
  Low: "bg-green-100 text-green-800",
  Medium: "bg-amber-100 text-amber-900",
  High: "bg-red-100 text-[#991B1B]",
};

export default function EconomistInsights({ analysis, scenarios, totalRevenue, onExport }) {
  const insights = analysis.insights || [];

  return (
    <div className="space-y-8">
      <SummaryBanner
        title="What a hospital economist would tell you"
        text="Eight to ten economist-style takeaways derived from the dataset — each explains what is happening, why it matters, the suggested action, and the expected financial impact."
        testId="insights-summary-banner"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="insights-grid">
        {insights.map((ins, i) => {
          const style = TONE[ins.tone] || TONE.info;
          const Icon = style.icon;
          const impactDollars =
            ins.impact_pct && totalRevenue
              ? (totalRevenue * ins.impact_pct) / 100
              : null;
          return (
            <article
              key={i}
              className={`border border-gray-200 border-l-4 ${style.border} ${style.bg} p-6`}
              data-testid={`insight-card-${i}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-gray-800" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge className={`rounded-none text-[10px] uppercase tracking-[0.18em] font-bold border-0 ${style.badge}`}>
                      {ins.tag}
                    </Badge>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                      Insight {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h4 className="font-display text-lg font-bold tracking-tight text-gray-950 leading-snug">
                    {ins.title}
                  </h4>
                </div>
              </div>

              <dl className="mt-5 space-y-3 text-sm pl-14">
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    What
                  </dt>
                  <dd className="text-gray-900 mt-1">{ins.what}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    Why it matters
                  </dt>
                  <dd className="text-gray-700 mt-1 leading-relaxed">{ins.why}</dd>
                </div>
                <div>
                  <dt className="text-[10px] uppercase tracking-[0.2em] text-[#5E3BFF] font-bold">
                    Action
                  </dt>
                  <dd className="text-gray-900 font-medium mt-1">{ins.action}</dd>
                </div>
                {impactDollars && (
                  <div className="pt-3 border-t border-gray-200 flex items-baseline gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                      Expected impact
                    </span>
                    <span className="font-display font-black text-lg text-[#16A34A] tracking-tighter">
                      {fmtCurrency(impactDollars)}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({fmtPct(ins.impact_pct, 1)} of revenue)
                    </span>
                  </div>
                )}
              </dl>
            </article>
          );
        })}
      </div>

      {!insights.length && (
        <EmptyBlock text="No insights could be generated — make sure revenue and department columns exist." />
      )}

      {scenarios?.length ? (
        <SectionBox
          title="What-If Scenarios"
          eyebrow="/ Model the upside"
          description="Simple operational scenarios with projected revenue gain, cost saving, operational impact and care-risk rating."
          testId="whatif-section"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="whatif-grid">
            {scenarios.map((s, i) => {
              const Icon = SCENARIO_ICON[s.icon] || Sparkles;
              return (
                <article
                  key={s.id}
                  className="border border-gray-200 p-6 bg-white hover:border-[#5E3BFF] transition-colors group"
                  data-testid={`whatif-card-${s.id}`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 bg-[#5E3BFF] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                    </div>
                    <Badge
                      className={`rounded-none text-[10px] uppercase tracking-[0.18em] font-bold border-0 ${
                        RISK_COLOR[s.care_risk] || RISK_COLOR.Low
                      }`}
                    >
                      {s.care_risk} risk
                    </Badge>
                  </div>
                  <h4 className="font-display text-lg font-bold tracking-tight text-gray-950 leading-snug mb-4">
                    {s.title}
                  </h4>

                  <div className="space-y-3 mb-5">
                    {s.revenue_gain > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                          Revenue gain
                        </div>
                        <div className="font-display font-black text-xl text-[#16A34A] tracking-tighter mt-0.5">
                          {fmtCurrency(s.revenue_gain)}
                        </div>
                      </div>
                    )}
                    {s.cost_saving > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                          Cost saving
                        </div>
                        <div className="font-display font-black text-xl text-[#5E3BFF] tracking-tighter mt-0.5">
                          {fmtCurrency(s.cost_saving)}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-100 space-y-1.5 text-xs">
                    <div className="text-gray-500 uppercase tracking-[0.2em] font-bold text-[10px]">
                      Operational impact
                    </div>
                    <div className="text-gray-700">{s.operational_impact}</div>
                    <div className="text-gray-500 pt-2 leading-relaxed">{s.rationale}</div>
                  </div>
                </article>
              );
            })}
          </div>
        </SectionBox>
      ) : null}

      <div
        className="border border-gray-200 p-6 bg-gray-50/60 flex items-start justify-between gap-4 flex-wrap no-print"
        data-testid="insights-export-banner"
      >
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#5E3BFF] mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="font-semibold text-gray-900">Share with the C-suite</div>
            <div className="text-sm text-gray-600 mt-1 max-w-lg">
              Export insights + scenarios as a PDF using the browser print dialog, or download a
              plain-text executive summary.
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => onExport("pdf")}
            className="bg-[#5E3BFF] text-white hover:bg-[#4A28E8] rounded-none h-10"
            data-testid="export-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Print / PDF
          </Button>
          <Button
            onClick={() => onExport("txt")}
            variant="outline"
            className="rounded-none h-10 border-gray-300 hover:bg-gray-50"
            data-testid="export-txt-btn"
          >
            <ArrowRight className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Download summary (.txt)
          </Button>
        </div>
      </div>
    </div>
  );
}
